/* Vivre fit core -- size charts and fit rules, shared by the server and the
 * storefront.
 *
 * ONE file, two places: sizing.js require()s it for Vivre Assist, and the
 * theme loads it as assets/vivre-fit-core.js for the "Your fit" size badge.
 * The repo copy is the mirror of the theme asset (see the theme SOP in
 * CLAUDE.md) -- change it here, upload it to the theme, and both agree.
 *
 * Keep it plain ASCII: the Shopify MCP rewrites some escapes on upload.
 * No DOM, no Node APIs, no optional chaining (older mobile Safari).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.VivreFitCore = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var CM_PER_IN = 2.54;
  var FIELDS = ['bust', 'underbust', 'waist', 'hip'];

  // Plausible adult human measurements, in inches. A value outside these is a
  // typo or a wrong unit -- we refuse to size off it rather than guessing.
  var PLAUSIBLE_IN = {
    bust:      [26, 60],
    underbust: [22, 55],
    waist:     [20, 55],
    hip:       [28, 65]
  };

  function inRange(v, r) { return v >= r[0] && v <= r[1]; }

  // -- Chart text out of a product description --------------------------------
  // Each product's chart lives in its own body_html, inside a hidden
  // <div style="display:none"> or <span class="bot-data"> block.

  function extractChartText(bodyHtml) {
    if (!bodyHtml) return null;

    var blocks = [];
    var hiddenDivRegex = /<div[^>]*style=["'][^"']*display:\s*none[^"']*["'][^>]*>([\s\S]*?)<\/div>/gi;
    var botDataSpanRegex = /<span[^>]*class=["'][^"']*bot-data[^"']*["'][^>]*>([\s\S]*?)<\/span>/gi;

    var match;
    while ((match = hiddenDivRegex.exec(bodyHtml))) blocks.push(match[1]);
    while ((match = botDataSpanRegex.exec(bodyHtml))) blocks.push(match[1]);
    if (blocks.length === 0) return null;

    var text = blocks
      .map(function (b) {
        return b
          .replace(/\s+/g, ' ')                    // collapse Shopify's own pretty-printed newlines first
          .replace(/<\/(tr|li|p|div)>/gi, '\n')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/(td|th)>/gi, ' | ')
          .replace(/<[^>]+>/g, '')
          .split('\n')
          .map(function (line) { return line.replace(/\s+/g, ' ').replace(/^\s*\|\s*/, '').replace(/\s*\|\s*$/, '').trim(); })
          .filter(Boolean)
          .join('\n');
      })
      .join('\n')
      .trim();

    return text || null;
  }

  // -- Chart parsing ------------------------------------------------------------
  // Two shapes exist in the catalogue:
  //   table:   Size | Bust | Under Bust | Waist | Hip
  //            2 | 30-32 | 26-28 | 24-26 | 33-35
  //   inline:  Size 2: Waist 24-26 | Hip 33-35 | Size 4: Waist 26-28 | ...
  // and a chart can be split by colourway ("Denim colourway size guide",
  // "Size guide for Cherry Cola colourway") with DIFFERENT bands per colour.

  var COLUMN_ALIASES = [
    ['size',      /^size$/],
    ['underbust', /^under[\s-]?(?:bust|band)$/],
    ['bust',      /^(?:bust|chest)$/],
    ['waist',     /^waist$/],
    ['hip',       /^hips?$/]
  ];

  function columnField(header) {
    var h = String(header || '').toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z\s-]/g, '').trim();
    for (var i = 0; i < COLUMN_ALIASES.length; i++) if (COLUMN_ALIASES[i][1].test(h)) return COLUMN_ALIASES[i][0];
    return null;
  }

  // Curly quote and en dash built from char codes, so this file stays ASCII
  // (the Shopify MCP rewrites escapes and non-ASCII on upload).
  var RDQUO = String.fromCharCode(0x201d);
  var NDASH = String.fromCharCode(0x2013);
  var QUOTES = new RegExp('[' + RDQUO + '"\']', 'g');

  function parseRange(cell) {
    var nums = String(cell || '').replace(QUOTES, '').match(/\d+(?:\.\d+)?/g);
    if (!nums) return null;
    var lo = parseFloat(nums[0]);
    var hi = nums.length > 1 ? parseFloat(nums[1]) : lo;
    return hi >= lo ? [lo, hi] : [hi, lo];
  }

  // Rows keyed by size, in first-seen order; a size seen twice (two tables
  // in one section) has its bands merged rather than listed twice.
  function addRow(sizes, size, bands) {
    if (!Object.keys(bands).length) return;
    for (var i = 0; i < sizes.length; i++) {
      if (sizes[i].size === size) {
        for (var f in bands) if (!sizes[i].bands[f]) sizes[i].bands[f] = bands[f];
        return;
      }
    }
    sizes.push({ size: size, bands: bands });
  }

  function parseTableRows(lines) {
    var cols = null;
    var sizes = [];
    lines.forEach(function (line) {
      if (line.indexOf('|') === -1) return;
      var cells = line.split('|').map(function (c) { return c.trim(); });

      var mapped = cells.map(columnField);
      // A header row names a Size column plus a measurement column. A second
      // header in the same section starts a second table.
      if (mapped.indexOf('size') !== -1 && mapped.some(function (f) { return f && f !== 'size'; })) {
        cols = mapped;
        return;
      }
      if (!cols || cells.length !== cols.length) return;

      var size = cells[cols.indexOf('size')];
      if (!size || size.length > 6) return;

      var bands = {};
      cols.forEach(function (f, i) {
        if (!f || f === 'size') return;
        var r = parseRange(cells[i]);
        if (r) bands[f] = r;
      });
      addRow(sizes, size, bands);
    });
    return sizes;
  }

  var INLINE_ROW = /Size\s+(\w{1,6})\s*:\s*([\s\S]*?)(?=Size\s+\w{1,6}\s*:|[A-Z][A-Z ]{3,}:|$)/g;
  var INLINE_BAND = new RegExp('(under\\s*bust|bust|chest|waist|hips?)\\s*:?\\s*(\\d+(?:\\.\\d+)?)\\s*(?:-|' + NDASH + '|to)\\s*(\\d+(?:\\.\\d+)?)', 'gi');

  function parseInlineRows(text) {
    var sizes = [];
    var m;
    INLINE_ROW.lastIndex = 0;
    while ((m = INLINE_ROW.exec(text))) {
      var bands = {};
      var b;
      INLINE_BAND.lastIndex = 0;
      while ((b = INLINE_BAND.exec(m[2]))) {
        var field = columnField(b[1]);
        var lo = parseFloat(b[2]), hi = parseFloat(b[3]);
        if (field && !bands[field]) bands[field] = hi >= lo ? [lo, hi] : [hi, lo];
      }
      addRow(sizes, m[1], bands);
      if (m[0] === '') INLINE_ROW.lastIndex++;
    }
    return sizes;
  }

  // "Denim colourway size guide (inches):" -> "denim"
  // "Size guide for all Ripple colourways (inches):" -> null (applies to all)
  function isColourwayHeading(line) {
    return /colou?rways?\b/i.test(line) && line.indexOf('|') === -1 && line.length < 120;
  }

  function colourPhrase(heading) {
    if (!heading || /\ball\b/i.test(heading)) return null;
    var p = normalise(heading
      .replace(/\((?:inches|inch|in|cm)\)/gi, ' ')
      .replace(/\b(size|guide|for|the|colou?rways?|inches|measurements?|chart)\b/gi, ' '));
    return p || null;
  }

  function normalise(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function sameSizes(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  // Returns every distinct chart in the text: [{ colour, unit, sizes }].
  // `colour` is the colourway phrase it applies to, or null for "all".
  // Colourway sections with identical bands are folded into one.
  function parseCharts(chartText) {
    if (!chartText) return [];

    var lines = String(chartText).split('\n').map(function (l) { return l.trim(); }).filter(Boolean);
    var sections = [{ heading: null, lines: [] }];
    lines.forEach(function (line) {
      if (isColourwayHeading(line)) sections.push({ heading: line, lines: [] });
      else sections[sections.length - 1].lines.push(line);
    });

    // Charts are inches unless they say otherwise
    var unit = /\bcms?\b|centimet/i.test(chartText) && !/inch|"/i.test(chartText) && chartText.indexOf(RDQUO) === -1 ? 'cm' : 'in';

    var charts = [];
    sections.forEach(function (s) {
      var sizes = parseTableRows(s.lines);
      if (!sizes.length) sizes = parseInlineRows(s.lines.join(' '));
      if (!sizes.length) return;
      var colour = colourPhrase(s.heading);
      for (var i = 0; i < charts.length; i++) {
        if (sameSizes(charts[i].sizes, sizes)) {
          // Same bands under two colourways: one chart that covers both
          charts[i].colours = charts[i].colours && colour ? charts[i].colours.concat([colour]) : null;
          return;
        }
      }
      charts.push({ colours: colour ? [colour] : null, unit: unit, sizes: sizes });
    });
    return charts;
  }

  // The chart for one colour. A single distinct chart applies to every
  // colour. With several, the colour must name one -- the longest matching
  // colourway wins, so "Classic Black (Ribbed)" beats "Classic Black".
  // Returns null when it can't tell: guessing would size off the wrong bands.
  function chartForColour(charts, colour) {
    if (!charts || !charts.length) return null;
    if (charts.length === 1) return charts[0];

    var want = normalise(colour);
    var best = null, bestLen = -1, fallback = null;
    charts.forEach(function (c) {
      if (!c.colours) { fallback = fallback || c; return; }
      c.colours.forEach(function (phrase) {
        if (!want) return;
        var hit = want === phrase || (' ' + want + ' ').indexOf(' ' + phrase + ' ') !== -1;
        if (hit && phrase.length > bestLen) { best = c; bestLen = phrase.length; }
      });
    });
    return best || fallback;
  }

  // Same, but the colour comes from free text (a chat message).
  function chartForText(charts, text) {
    if (!charts || !charts.length) return null;
    if (charts.length === 1) return charts[0];
    var hay = ' ' + normalise(text) + ' ';
    var best = null, bestLen = -1;
    charts.forEach(function (c) {
      (c.colours || []).forEach(function (phrase) {
        if (hay.indexOf(' ' + phrase + ' ') !== -1 && phrase.length > bestLen) { best = c; bestLen = phrase.length; }
      });
    });
    return best;
  }

  // Back-compat single-chart view: the only chart, or null when colourways
  // disagree (callers that need colour use parseCharts + chartForColour).
  function parseSizeChart(chartText) {
    var charts = parseCharts(chartText);
    return charts.length === 1 ? charts[0] : null;
  }

  // -- Garment type -------------------------------------------------------------

  var TOP_WORDS = /\b(bra|bralette|tank|top|tee|shirt|crop|jacket|hoodie|pullover|sweater|sleeve|cardigan)\b/i;
  var BOTTOM_WORDS = /\b(short|shorts|tight|tights|legging|leggings|pant|pants|jogger|joggers|skirt|skort|bikini|brief|briefs|thong)\b/i;

  function garmentType(title) {
    var isTop = TOP_WORDS.test(title || '');
    var isBottom = BOTTOM_WORDS.test(title || '');
    if (isTop && !isBottom) return 'top';
    if (isBottom && !isTop) return 'bottom';
    return 'other';
  }

  var RELEVANT = { top: ['bust', 'underbust'], bottom: ['waist', 'hip'], other: ['bust', 'underbust', 'waist', 'hip'] };

  // "Recommended to size down if you're between your usual Vivre sizes" -> 'down'.
  // "Between sizes? Size down for ..., or size up for ..." names both -> null.
  function betweenLean(bodyHtml) {
    var text = String(bodyHtml || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
    var lean = null;
    text.split(/[.!?\n]/).forEach(function (sentence) {
      if (!/\bbetween\b/i.test(sentence)) return;
      var down = /\bsize down\b/i.test(sentence), up = /\bsize up\b/i.test(sentence);
      if (down !== up && !lean) lean = down ? 'down' : 'up';
    });
    return lean;
  }

  // -- Band matching ------------------------------------------------------------

  function matchField(chart, field, value) {
    var rows = chart.sizes.filter(function (r) { return r.bands[field]; });
    if (!rows.length) return { kind: 'no-column' };

    var hitRows = rows.filter(function (r) { return inRange(value, r.bands[field]); });
    if (hitRows.length === 1) return { kind: 'exact', size: hitRows[0].size, band: hitRows[0].bands[field] };
    if (hitRows.length > 1) return { kind: 'between', rows: hitRows };

    var sorted = rows.slice().sort(function (a, b) { return a.bands[field][0] - b.bands[field][0]; });
    var first = sorted[0], last = sorted[sorted.length - 1];
    if (value < first.bands[field][0]) return { kind: 'below', rows: sorted };
    if (value > last.bands[field][1]) return { kind: 'above', rows: sorted };

    // Falls in a gap between two adjacent bands
    var lower = sorted.slice().reverse().filter(function (r) { return r.bands[field][1] < value; })[0];
    var upper = sorted.filter(function (r) { return r.bands[field][0] > value; })[0];
    return { kind: 'between', rows: [lower, upper].filter(Boolean) };
  }

  function sizeIndex(chart, size) {
    for (var i = 0; i < chart.sizes.length; i++) if (chart.sizes[i].size === size) return i;
    return -1;
  }

  // -- The decision ---------------------------------------------------------------
  // Structured, so the chat can phrase it and the product page can badge it
  // from the same rules. Statuses:
  //   size     one size. `confirm` names a missing measurement that would confirm
  //            it; `note` is a one-size-apart nuance.
  //   between  two sizes on `field`
  //   need     not enough to decide; `need` lists what to ask for
  //   apart    measurements two or more sizes apart; `sizes` maps field -> size
  //   mixed    'other' garments whose measurements point at different sizes
  //   outside  outside every band this product is made in

  function decide(chart, type, matches) {
    function m(f) { return matches[f] || null; }
    function kind(f) { return m(f) ? m(f).kind : null; }
    function single(f) { return kind(f) === 'exact' ? m(f).size : null; }
    function ends(f) { var rows = m(f).rows; return [rows[0].size, rows[rows.length - 1].size]; }

    var entries = Object.keys(matches).map(function (f) { return [f, matches[f]]; });
    var anyIn = entries.some(function (e) { return e[1].kind === 'exact' || e[1].kind === 'between'; });
    var anyOut = entries.some(function (e) { return e[1].kind === 'below' || e[1].kind === 'above'; });
    if (anyOut && !anyIn) return { status: 'outside' };

    if (type === 'top') {
      if (kind('bust') === 'between') return { status: 'between', field: 'bust', sizes: ends('bust') };
      var bust = single('bust');
      var under = single('underbust');
      if (bust && under) {
        var d = sizeIndex(chart, under) - sizeIndex(chart, bust);
        if (d === 0) return { status: 'size', size: bust, basis: ['bust', 'underbust'] };
        if (Math.abs(d) === 1) return { status: 'size', size: bust, basis: ['bust', 'underbust'], note: d < 0 ? 'band-snug' : 'band-loose' };
        return { status: 'apart', sizes: { bust: bust, underbust: under } };
      }
      if (bust) return { status: 'size', size: bust, basis: ['bust'] };
      if (under) return { status: 'size', size: under, basis: ['underbust'], confirm: 'bust' };
      if (kind('underbust') === 'between') return { status: 'need', need: ['bust'], reason: 'underbust-between' };
      return { status: 'need', need: ['bust', 'underbust'] };
    }

    if (type === 'bottom') {
      var waist = single('waist');
      var hip = single('hip');
      if (waist && hip) {
        var wi = sizeIndex(chart, waist), hi = sizeIndex(chart, hip);
        if (wi === hi) return { status: 'size', size: waist, basis: ['waist', 'hip'] };
        if (Math.abs(wi - hi) === 1) {
          return hi > wi
            ? { status: 'size', size: hip, basis: ['waist', 'hip'], note: 'fit-to-hips' }
            : { status: 'size', size: waist, basis: ['waist', 'hip'], note: 'fit-to-waist' };
        }
        return { status: 'apart', sizes: { waist: waist, hip: hip } };
      }
      if (kind('waist') === 'between' || kind('hip') === 'between') {
        var f = kind('waist') === 'between' ? 'waist' : 'hip';
        return { status: 'between', field: f, sizes: ends(f) };
      }
      if (waist) return { status: 'size', size: waist, basis: ['waist'], confirm: 'hip' };
      if (hip) return { status: 'size', size: hip, basis: ['hip'], confirm: 'waist' };
      return { status: 'need', need: ['waist', 'hip'] };
    }

    var exact = entries.filter(function (e) { return e[1].kind === 'exact'; });
    if (!exact.length) return { status: 'need', need: RELEVANT.other.slice() };
    var distinct = [];
    exact.forEach(function (e) { if (distinct.indexOf(e[1].size) === -1) distinct.push(e[1].size); });
    if (distinct.length === 1) return { status: 'size', size: distinct[0], basis: exact.map(function (e) { return e[0]; }) };
    var bySize = {};
    exact.forEach(function (e) { bySize[e[0]] = e[1].size; });
    return { status: 'mixed', sizes: bySize };
  }

  // -- Product page entry point ------------------------------------------------------
  // profile: { bust, underbust, waist, hip } in inches (any may be missing).
  // Returns the decision plus what the page needs to badge it:
  //   { status, highlight: [sizes], pick, lean, ... }  or  { status: 'no-chart' }

  function fitForProduct(opts) {
    var charts = parseCharts(extractChartText(opts.bodyHtml));
    if (!charts.length) return { status: 'no-chart' };
    var chart = chartForColour(charts, opts.colour);
    if (!chart) return { status: 'no-chart', reason: 'colour' };

    var profile = opts.profile || {};
    var type = garmentType(opts.title);
    var relevant = RELEVANT[type].filter(function (f) {
      return chart.sizes.some(function (r) { return r.bands[f]; });
    });

    var matches = {};
    relevant.forEach(function (f) {
      var v = Number(profile[f]);
      if (!v || !inRange(v, PLAUSIBLE_IN[f])) return;
      matches[f] = matchField(chart, f, chart.unit === 'cm' ? v * CM_PER_IN : v);
    });

    var out = decide(chart, type, matches);
    out.type = type;
    out.relevant = relevant;
    out.highlight = [];
    if (out.status === 'size') out.highlight = [out.size];
    if (out.status === 'between') {
      out.highlight = out.sizes.slice();
      out.lean = betweenLean(opts.bodyHtml);
      if (out.lean) out.pick = out.lean === 'down' ? out.sizes[0] : out.sizes[out.sizes.length - 1];
    }
    if (out.status === 'mixed') {
      var seen = [];
      chart.sizes.forEach(function (r) {
        for (var f in out.sizes) if (out.sizes[f] === r.size && seen.indexOf(r.size) === -1) seen.push(r.size);
      });
      out.highlight = seen;
    }
    if (out.status === 'need') {
      out.need = out.need.filter(function (f) { return relevant.indexOf(f) !== -1; });
    }
    return out;
  }

  // -- Saved profile ----------------------------------------------------------------
  // What the customer typed -> what gets stored (customer metafield
  // vivre.fit_profile). Values are kept in inches for sizing, and as entered
  // (`entered`, in `unit`) so the form shows back exactly what they typed.
  // Returns { profile } or { errors: { field: message } }. Blank fields are
  // simply not saved; an all-blank form is { profile: null } (clear).

  function buildProfile(input) {
    input = input || {};
    var unit = input.unit === 'cm' ? 'cm' : 'in';
    var profile = { v: 1, unit: unit, entered: {} };
    var errors = {};
    var any = false;
    FIELDS.forEach(function (f) {
      var raw = input[f];
      if (raw === undefined || raw === null || String(raw).trim() === '') return;
      var n = Number(String(raw).replace(',', '.'));
      if (!isFinite(n) || n <= 0) { errors[f] = 'Enter a number'; return; }
      var inches = unit === 'cm' ? n / CM_PER_IN : n;
      if (!inRange(inches, PLAUSIBLE_IN[f])) {
        var r = PLAUSIBLE_IN[f];
        errors[f] = unit === 'cm'
          ? 'That looks off. Usually ' + Math.round(r[0] * CM_PER_IN) + '-' + Math.round(r[1] * CM_PER_IN) + 'cm'
          : 'That looks off. Usually ' + r[0] + '-' + r[1] + ' inches';
        return;
      }
      profile[f] = Math.round(inches * 100) / 100;
      profile.entered[f] = Math.round(n * 10) / 10;
      any = true;
    });
    if (Object.keys(errors).length) return { errors: errors };
    return { profile: any ? profile : null };
  }

  return {
    CM_PER_IN: CM_PER_IN,
    buildProfile: buildProfile,
    FIELDS: FIELDS,
    PLAUSIBLE_IN: PLAUSIBLE_IN,
    RELEVANT: RELEVANT,
    TOP_WORDS: TOP_WORDS,
    BOTTOM_WORDS: BOTTOM_WORDS,
    inRange: inRange,
    extractChartText: extractChartText,
    parseCharts: parseCharts,
    parseSizeChart: parseSizeChart,
    chartForColour: chartForColour,
    chartForText: chartForText,
    garmentType: garmentType,
    betweenLean: betweenLean,
    matchField: matchField,
    sizeIndex: sizeIndex,
    decide: decide,
    fitForProduct: fitForProduct
  };
});
